
import {
  AuthInviteCompleteRequest,
  PasswordResetCompleteRequest,
  TokenRefreshRequest,
} from '@flagsync/skyline';
import { UseMutationOptions, useMutation } from '@tanstack/react-query';

import { sky } from '@/services/api-client';
import { TokenTypeCode } from '@/services/types';
import { ServiceError } from '@/services/utils/service-error';

export interface VerifyAccountRequestVariables {
  email: string;
}

export interface ResetPasswordRequestVariables {
  email: string;
  isRetry: boolean;
}

export interface ResetPasswordCompleteVariables {
  email: string;
  password: string;
  token: string;
  organizationId?: number;
}

export interface InviteOrganizationCompleteVariables {
  email: string;
  password: string;
  token: string;
  organizationId: number;
}

export function useVerifyAccountRequest(
  options?: UseMutationOptions<
    void,
    ServiceError,
    VerifyAccountRequestVariables
  >,
) {
  return useMutation<void, ServiceError, VerifyAccountRequestVariables>({
    mutationFn: async (variables) => {
      const request: TokenRefreshRequest = {
        email: variables.email,
        tokenType: TokenTypeCode.VER_ACC,
      };
      await sky.post<void>('/auth/token/request', request);
    },
    ...options,
  });
}

export function useResetPasswordRequest(
  options?: UseMutationOptions<
    void,
    ServiceError,
    ResetPasswordRequestVariables
  >,
) {
  return useMutation<void, ServiceError, ResetPasswordRequestVariables>({
    mutationFn: async (variables) => {
      const request: TokenRefreshRequest = {
        email: variables.email,
        tokenType: TokenTypeCode.RES_PWD,
      };
      await sky.post<void>('/auth/token/request', request);
    },
    ...options,
  });
}

export function useResetPasswordComplete(
  options?: UseMutationOptions<
    void,
    ServiceError,
    ResetPasswordCompleteVariables
  >,
) {
  return useMutation<void, ServiceError, ResetPasswordCompleteVariables>({
    mutationFn: async (variables) => {
      const request: PasswordResetCompleteRequest = {
        email: variables.email,
        password: variables.password,
        token: variables.token,
      };
      await sky.post<void>('/auth/reset-password/complete', request);
    },
    ...options,
  });
}

export function useInviteOrganizationComplete(
  options?: UseMutationOptions<
    void,
    ServiceError,
    InviteOrganizationCompleteVariables
  >,
) {
  return useMutation<void, ServiceError, InviteOrganizationCompleteVariables>({
    mutationFn: async (variables) => {
      const request: AuthInviteCompleteRequest = {
        email: variables.email,
        password: variables.password,
        token: variables.token,
      };
      await sky.post<void>(
        `/invite/${variables.organizationId}/complete`,
        request,
      );
    },
    ...options,
  });
}
